import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Video, Smartphone, Music, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

const ALL_TOOLS = [
  {
    title: 'Ball Simulation Generator',
    description: 'Create hyper-realistic bouncing ball simulations with advanced physics controls.',
    href: '/ball-simulation',
    icon: Cpu,
    accent: 'text-cyan-600 bg-cyan-50 border-cyan-100',
  },
  {
    title: 'Physics Video Generator',
    description: 'Build professional physics-based video content for social media platforms.',
    href: '/physics-video-generator',
    icon: Video,
    accent: 'text-purple-600 bg-purple-50 border-purple-100',
  },
  {
    title: 'Faceless Video Generator',
    description: 'Scale your faceless YouTube channel with automated physics content.',
    href: '/faceless-video-generator',
    icon: Eye,
    accent: 'text-emerald-600 bg-emerald-50 border-emerald-100',
  },
  {
    title: 'YouTube Shorts Generator',
    description: 'Create viral-ready 9:16 vertical videos optimized for YouTube Shorts.',
    href: '/youtube-shorts-generator',
    icon: Smartphone,
    accent: 'text-red-600 bg-red-50 border-red-100',
  },
  {
    title: 'ASMR Video Generator',
    description: 'Generate oddly satisfying ASMR physics animations with precision sound sync.',
    href: '/asmr-video-generator',
    icon: Music,
    accent: 'text-amber-600 bg-amber-50 border-amber-100',
  },
];

interface RelatedToolsSectionProps {
  currentTool?: string; // href of current page to exclude
  className?: string;
}

export const RelatedToolsSection: React.FC<RelatedToolsSectionProps> = ({ currentTool, className }) => {
  const tools = ALL_TOOLS.filter(t => t.href !== currentTool);

  return (
    <section className={cn('py-24 px-6 bg-zinc-50/50 border-t border-zinc-100 relative overflow-hidden', className)}>
      <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-zinc-50 -z-10" />
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-xs uppercase tracking-[0.4em] font-bold text-zinc-400">Related Tools</h2>
          <h3 className="text-4xl md:text-5xl font-display font-black tracking-tighter uppercase text-zinc-950">
            Explore More <span className="text-cyan-600">Creator Tools</span>
          </h3>
          <p className="text-zinc-500 font-medium text-lg font-sans">
            Discover other powerful tools in our creator suite designed for viral content production.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <motion.div
                key={tool.title}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ duration: 0.2 }}
                className="group relative bg-white border border-zinc-200 rounded-3xl p-8 flex flex-col justify-between hover:shadow-lg transition-all duration-300"
              >
                <div className="space-y-6">
                  <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${tool.accent}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="space-y-3">
                    <h4 className="text-lg font-bold font-display text-zinc-900 group-hover:text-cyan-600 transition-colors uppercase tracking-tight">
                      {tool.title}
                    </h4>
                    <p className="text-sm text-zinc-500 leading-relaxed font-sans font-medium">
                      {tool.description}
                    </p>
                  </div>
                </div>

                <div className="pt-8">
                  <Link
                    to={tool.href}
                    className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-950 hover:text-cyan-600 transition-colors"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedToolsSection;
